import { useTranslation } from "react-i18next";

import { OperationIcon } from "@/components/cart/OperationIcon";
import { cx } from "@/lib/utils";
import type { Process } from "@/api/types";

interface ProcessSwitchProps {
  /** Landing-eligible file-driven processes, already sorted by
   *  ``landing_order`` (the caller owns filtering — see Landing's
   *  ``switchProcesses``). */
  processes: Process[];
  /** Slug of the currently selected process. */
  value: string;
  onChange: (slug: string) => void;
  /** Disable interaction — used while an upload is in-flight. */
  disabled?: boolean;
}

/**
 * Segmented process switch — the homepage "step 1" control that decides
 * which analyzer the hero dropzone feeds ("Volba procesu V1").
 *
 * Radio semantics: exactly one process is always active, never an empty
 * state. Registry-driven — options come from ``GET /processes``
 * (display name, platform icon, ``landing_order``), so a new module
 * lights up a new segment without a deploy.
 *
 * A single-process tenant renders a static label instead of a control —
 * the visitor sees what the shop makes without an extra decision.
 */
export function ProcessSwitch({
  processes,
  value,
  onChange,
  disabled = false,
}: ProcessSwitchProps): JSX.Element | null {
  const { t } = useTranslation("common");

  if (processes.length === 0) return null;

  if (processes.length === 1) {
    const only = processes[0];
    return (
      <span
        className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-4 py-1.5 text-sm font-semibold text-ink"
        data-testid="process-switch-static"
      >
        <OperationIcon
          iconUrl={only.icon_url}
          slug={only.slug}
          displayName={only.display_name}
          size={18}
        />
        {only.display_name}
      </span>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label={t("home.processSwitch.groupLabel")}
      className="inline-flex w-full max-w-md flex-col gap-1 rounded-xl border border-border bg-surface-2 p-1 sm:w-auto sm:max-w-none sm:flex-row"
      data-testid="process-switch"
    >
      {processes.map((p) => {
        const active = p.slug === value;
        return (
          <button
            key={p.slug}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(p.slug)}
            className={cx(
              "inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm transition",
              active
                ? "border-border bg-surface font-semibold text-ink shadow-sm"
                : "border-transparent font-medium text-ink-3 hover:text-ink-2",
            )}
            data-testid={`process-switch-option-${p.slug}`}
          >
            <OperationIcon
              iconUrl={p.icon_url}
              slug={p.slug}
              displayName={p.display_name}
              size={18}
            />
            {p.display_name}
          </button>
        );
      })}
    </div>
  );
}
